import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Shield } from 'lucide-react';
import {
  BodyText,
  Button,
  Input,
} from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';

// Sprint C8 Lane B: Microsoft Entra ID SSO is hidden until aaim-core ships a
// Microsoft auth strategy. Without the flag, the button is unreachable so an
// internal user cannot click into a confusing failure path. Flip to "true"
// only when the post-C8 Entra wiring lands. See vite-env.d.ts for context.
const ENABLE_MICROSOFT_SSO = import.meta.env.VITE_ENABLE_MICROSOFT_SSO === 'true';

export function LoginPage() {
  const navigate = useNavigate();
  const { oauthLoading, oauthError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const urlError = new URLSearchParams(window.location.search).get('error');
  const urlMessage = new URLSearchParams(window.location.search).get('message');
  const displayError = error ?? oauthError ?? (urlError ? (urlMessage ?? 'Authentication failed') : null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await useAuthStore.getState().login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    useAuthStore.getState().loginWithGoogle().catch(() => {});
  };

  const handleMicrosoftLogin = () => {
    useAuthStore.getState().loginWithProvider('microsoft').catch(() => {});
  };

  // Sprint C7 (2026-04-27): the prior `handleDemoLogin` button injected a baked
  // SUPER_ADMIN session for `admin@aaim.com` and a long-lived dev JWT signed
  // with the well-known dev secret. Removed because it bypassed Google SSO and
  // existed as live attack surface in any environment that shipped DEV=true.
  // Aaim Platform operators authenticate exclusively via Google SSO of the
  // canonical Aaim Workspace (aaim.com / aaim.app / aaimplatform.com).
  // Sprint C8 wires a true e2etesting user backed by the aaim-core auth schema.

  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card with red top accent */}
        <div className="bg-surface rounded shadow-sm border border-recessed overflow-hidden">
          <div className="h-1 bg-accent" />
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
                alt="Aaim"
                className="h-8"
              />
            </div>

            {/* Header with shield icon */}
            <div className="flex items-center justify-center gap-2 mb-1">
              <Shield size={18} className="text-accent" />
              <h1 className="text-2xl font-sans font-semibold text-primary tracking-tight">
                Platform Admin
              </h1>
            </div>
            <p className="text-sm font-sans text-tertiary text-center mb-8">
              Administration Console
            </p>

            {/* Error */}
            {displayError && (
              <div className="mb-6 p-3 bg-error/10 border border-error/20 rounded text-error text-sm">
                {displayError}
              </div>
            )}

            {/* Login form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email address"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@aaim.com"
                autoComplete="email"
                required
              />

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-2"
                >
                  Password
                  <span className="text-error ml-1">*</span>
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-tertiary hover:text-secondary transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                size="large"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {/* OAuth divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-recessed" />
              <span className="px-4 text-quaternary text-label-xs font-semibold uppercase tracking-[0.15em]">
                Or continue with
              </span>
              <div className="flex-1 border-t border-recessed" />
            </div>

            {/* SSO Providers */}
            <div className="space-y-3">
              {/* Google OAuth */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={oauthLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-recessed rounded bg-surface hover:bg-elevated transition-colors text-sm font-medium text-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oauthLoading ? (
                  <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    {/* design-token-skip: Google brand mark (exact brand fills required) */}
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z" />
                    {/* design-token-skip: Google brand mark */}
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                {oauthLoading ? 'Redirecting...' : 'Sign in with Google'}
              </button>

              {/* Sprint C8 Lane B3: domain hint for canonical Aaim Identity Provider */}
              <BodyText variant="ui" size="xs" color="tertiary" className="text-center px-2">
                Use your @aaim.com, @aaim.app, or @aaimplatform.com Google account
              </BodyText>

              {/* Microsoft Entra ID -- gated by VITE_ENABLE_MICROSOFT_SSO,
                  hidden until aaim-core ships a Microsoft auth strategy. */}
              {ENABLE_MICROSOFT_SSO && (
                <button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  disabled={oauthLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-recessed rounded bg-surface hover:bg-elevated transition-colors text-sm font-medium text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {oauthLoading ? (
                    <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 21 21">
                      {/* design-token-skip: Microsoft brand mark (exact brand fills required) */}
                      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                      {/* design-token-skip: Microsoft brand mark */}
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                      {/* design-token-skip: Microsoft brand mark */}
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                      {/* design-token-skip: Microsoft brand mark */}
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                    </svg>
                  )}
                  {oauthLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
                </button>
              )}
            </div>

            {/* Security footer */}
            <div className="flex items-center justify-center gap-1.5 mt-6 pt-4 border-t border-recessed">
              <Lock size={12} className="text-quaternary" />
              <span className="text-caption-sm text-quaternary">
                Secured with 256-bit encryption
              </span>
            </div>
          </div>
        </div>

        {/* Below-card footer */}
        <div className="text-center mt-4">
          <p className="text-xs text-quaternary">
            Access restricted to authorized platform administrators
          </p>
        </div>

        {/* Sprint C7: the dev "Demo Login (Super Admin)" button was removed. */}
        {/* Aaim Platform admin auth is Google SSO only; no bypass shipped. */}
      </div>
    </div>
  );
}
